import { NgZone } from '@angular/core';

/** Which side of a pinned header pair the user is scrolling sideways, so only that side writes. */
type ScrollDriver = 'table' | 'copy';

/**
 * Keeps a pinned copy of a table's header row lined up with the real one (RA-2838).
 *
 * The copy sits in its own sideways-scrolling viewport, so a sticky first column keeps working in
 * it exactly as in the table. This class mirrors the real header's column widths onto the copy
 * and keeps the two viewports' `scrollLeft` in step in both directions, so a swipe on either one
 * scrolls the other.
 *
 * - Widths are measured per cell, fractionally: with auto layout a column can widen while the
 *   table's total stays the same, and every edge has to land within a pixel of the real one. The
 *   copy's table should use `table-layout: fixed` so the widths are honoured exactly.
 * - Whichever side the user last touched drives the other. Writing `scrollLeft` back into the
 *   side being flung would stop its momentum on iOS.
 * - Everything runs outside the Angular zone; nothing here needs change detection.
 *
 * Owners call `layout()` whenever the widths may have moved and `destroy()` when either element
 * goes away. `onResize` fires when a real header cell or the table resizes on its own, e.g. a
 * web font loading or a sort changing which values are widest.
 */
export class PinnedHeaderMirror {
  private driver: ScrollDriver = 'table';
  private readonly listeners: Array<() => void> = [];
  private resizeObserver: ResizeObserver | null = null;
  private observed: Array<HTMLElement> = [];

  /** A native `<table>`, or a flex `mat-table` (the copy is always native). */
  private static table(container: HTMLElement): HTMLElement | null {
    return container.querySelector(':scope > table, :scope > mat-table');
  }

  /**
   * The cells of a table's own header rows, or a mat-table's; `thead` only, since a body can hold a
   * `th` band. The first `thead` only, since a table split into groups repeats the same header for
   * each group.
   */
  private static headerCells(table: HTMLElement): Array<HTMLElement> {
    return Array.from(table.querySelectorAll(':scope > thead:first-of-type > tr > th, :scope > mat-header-row > mat-header-cell'));
  }

  /**
   * The header cell that ends at each column, walking the header rows as a rowspan/colspan grid,
   * e.g. a group's columns from its second row and an ungrouped column from its two-row cell.
   * Empty for a single header row, whose cells already map one to one onto columns.
   */
  private static leafCells(table: HTMLElement): Array<HTMLTableCellElement> {
    const rows = Array.from(table.querySelectorAll(':scope > thead:first-of-type > tr')) as Array<HTMLTableRowElement>;
    if (rows.length < 2) {
      return [];
    }
    const grid: Array<Array<HTMLTableCellElement>> = rows.map(() => []);
    rows.forEach((row, r) => {
      let c = 0;
      Array.from(row.cells).forEach(cell => {
        while (grid[r][c]) {
          c++;
        }
        for (let i = 0; i < (cell.rowSpan || 1) && r + i < rows.length; i++) {
          for (let j = 0; j < (cell.colSpan || 1); j++) {
            grid[r + i][c + j] = cell;
          }
        }
        c += cell.colSpan || 1;
      });
    });
    return grid.at(-1);
  }

  /** Pins a width on a copied element, `!important` so it beats a page's own `!important` widths. */
  private static setWidth(element: HTMLElement, width: string): void {
    ['width', 'min-width', 'max-width'].forEach(property => {
      if (width) {
        element.style.setProperty(property, width, 'important');
      } else {
        element.style.removeProperty(property);
      }
    });
  }

  /**
   * @param scroller: the real table's horizontally scrolling container
   * @param viewport: the copy's horizontally scrolling container
   * @param ngZone: used to keep the listeners outside the Angular zone
   * @param onResize: called when the real header row resizes without a page event
   */
  constructor(
    public readonly scroller: HTMLElement,
    public readonly viewport: HTMLElement,
    ngZone: NgZone,
    onResize: () => void
  ) {
    ngZone.runOutsideAngular(() => {
      const listen = (target: HTMLElement, event: string, handler: () => void) => {
        target.addEventListener(event, handler, { passive: true });
        this.listeners.push(() => target.removeEventListener(event, handler));
      };
      const drive = (driver: ScrollDriver) => () => {
        this.driver = driver;
      };
      const follow = (driver: ScrollDriver) => () => {
        if (this.driver === driver) {
          this.syncScroll();
        }
      };
      ['touchstart', 'pointerdown', 'wheel'].forEach(event => {
        listen(scroller, event, drive('table'));
        listen(viewport, event, drive('copy'));
      });
      listen(scroller, 'scroll', follow('table'));
      listen(viewport, 'scroll', follow('copy'));

      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => onResize());
      }
    });
    this.observe();
  }

  /**
   * Mirrors the real table's width and each header cell's width onto the copy, then its offset. Every
   * width is read before any is written, so the pass costs one layout rather than one per column.
   */
  public layout(): void {
    const realTable = PinnedHeaderMirror.table(this.scroller);
    const copyTable = PinnedHeaderMirror.table(this.viewport);
    if (!realTable || !copyTable) {
      return;
    }
    this.observe();
    const tableWidth = realTable.getBoundingClientRect().width;
    const cellWidths = PinnedHeaderMirror.headerCells(realTable).map(cell => cell.getBoundingClientRect().width);
    const columnWidths = PinnedHeaderMirror.leafCells(realTable)
      .map(cell => cell.getBoundingClientRect().width / (cell.colSpan || 1));

    copyTable.style.width = `${tableWidth}px`;
    PinnedHeaderMirror.headerCells(copyTable).forEach((cell, i) => {
      PinnedHeaderMirror.setWidth(cell, cellWidths[i] === undefined ? '' : `${cellWidths[i]}px`);
    });
    this.layoutColumns(copyTable, columnWidths);
    this.syncScroll();
  }

  public destroy(): void {
    this.listeners.forEach(remove => remove());
    this.resizeObserver?.disconnect();
  }

  /**
   * With two header rows, fixed layout takes the column widths from the first row, where a group
   * cell spans several columns, and splits each group evenly. A `<colgroup>` on the copy gives it
   * every column's own width instead; it is removed again when the header is back to one row.
   */
  private layoutColumns(copyTable: HTMLElement, columnWidths: Array<number>): void {
    let colgroup = copyTable.querySelector(':scope > colgroup.pinned-columns');
    if (!columnWidths.length) {
      colgroup?.remove();
      return;
    }
    if (!colgroup) {
      colgroup = document.createElement('colgroup');
      colgroup.className = 'pinned-columns';
      copyTable.insertBefore(colgroup, copyTable.firstChild);
    }
    while (colgroup.children.length > columnWidths.length) {
      colgroup.lastChild.remove();
    }
    while (colgroup.children.length < columnWidths.length) {
      colgroup.appendChild(document.createElement('col'));
    }
    columnWidths.forEach((width, i) => {
      PinnedHeaderMirror.setWidth(colgroup.children[i] as HTMLElement, `${width}px`);
    });
  }

  /**
   * Watches the real table and each of its header cells, re-pointing when the cells change, e.g.
   * columns added or removed by a toggle. `observe()` reports straight away, so it is only called
   * for elements not already watched, or the callback would re-enter `layout()` endlessly.
   */
  private observe(): void {
    const realTable = PinnedHeaderMirror.table(this.scroller);
    if (!this.resizeObserver || !realTable) {
      return;
    }
    const next = [realTable, ...PinnedHeaderMirror.headerCells(realTable)];
    if (next.length === this.observed.length && next.every((element, i) => element === this.observed[i])) {
      return;
    }
    this.resizeObserver.disconnect();
    next.forEach(element => this.resizeObserver.observe(element));
    this.observed = next;
  }

  /** Copies `scrollLeft` from the driving side to the other, skipping a write that changes nothing. */
  private syncScroll(): void {
    const [from, to] = this.driver === 'table' ? [this.scroller, this.viewport] : [this.viewport, this.scroller];
    if (Math.abs(to.scrollLeft - from.scrollLeft) >= 0.5) {
      to.scrollLeft = from.scrollLeft;
    }
  }
}
